import React, { useEffect, useLayoutEffect, useState } from 'react';
import useSceneController from './use-scene-controller';
import { Surface } from 'gl-react-dom';
import GLTransition from 'react-gl-transition';
import GLTransitions from 'gl-transitions';
import styles from './index.less';
import * as dat from 'dat.gui';
import 'dat.gui.ease/dist/dat.gui.ease.css';
import GSAPv3Middleware from 'dat.gui.ease.gsap.v3';

import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import { useDebounce } from 'ahooks';

import wrapStore from '../../utils/wrap-store';
import SceneStore from './store';

const datGuiEase = require('dat.gui.ease');

gsap.registerPlugin(CustomEase);
// 右上角GUi面板
datGuiEase.extend(dat).use(new GSAPv3Middleware(CustomEase));

const GLTransitionsNames = GLTransitions.map(item => item.name);

const getScenes = list => {
  let scene = {};
  if (list?.length > 0) {
    list.map((item, index) => {
      scene[item.key] = item;
    });
  }
  return scene;
};

/**
 * 设置场景属性
 * @param activeIndex
 * @returns {JSX.Element}
 * @constructor
 */
const Scene = ({ store, activeIndex, SCENE_LIST, isActive, effectId, options = {}, onComplete, customHeight }) => {
  store.use();
  const SCENES = getScenes(SCENE_LIST);

  // 使用options中的值或默认值
  const defaultTransitionName = options.transition || 'DreamyZoom';
  const transitionIndexFromName = GLTransitionsNames.indexOf(defaultTransitionName) >= 0 ? GLTransitionsNames.indexOf(defaultTransitionName) : effectId;

  const [transitionIndex, setTransitionIndex] = useState(transitionIndexFromName);

  const [delay, setDelay] = useState(options.delay || 0);
  const [duration, setDuration] = useState(options.duration || 2);
  const [ease, setEase] = useState(options.ease || 'power1.out');
  const debouncedEaseValue = useDebounce(ease, { wait: 500 });

  // Add state to track if component is ready to render
  const [isReady, setIsReady] = useState(false);

  const controller = useSceneController({
    SCENES: SCENES,
    initialScene: SCENE_LIST[0] || {},
    transition: GLTransitions[transitionIndex],
    initialProgress: 1
  });

  const { transitionParams } = controller;

  window.controller = controller;
  window.SCENE_LIST = SCENE_LIST;

  useEffect(() => {
    if (!window.datGui && !(DEPLOY_ENV && DEPLOY_ENV !== 'dev')) {
      const options = {
        transition: 'DreamyZoom',
        duration: 2,
        delay: 0,
        ease: 'power1.out'
      };
      const gui = new dat.GUI();
      window.datGui = gui;
      gui.add(options, 'transition', GLTransitionsNames).onChange(val => {
        const transitionIndex = GLTransitions.findIndex(item => item.name === val);
        setTransitionIndex(transitionIndex);
      });
      gui.add(options, 'duration').onChange(val => {
        setDuration(val);
      });
      gui.add(options, 'delay').onChange(val => {
        setDelay(val);
      });
      gui.addEase(options, 'ease').onChange(val => {
        setEase(val);
      });
    }

    return () => {
      window.datGui && window.datGui.destroy();
      window.datGui = undefined;
    };
  }, []);

  // Set ready state after a small delay to prevent initial flash
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useLayoutEffect(
    () => {
      if (typeof activeIndex === 'undefined' || !isActive || !SCENE_LIST || SCENE_LIST.length === 0) {
        return;
      }

      const currentScene = SCENE_LIST[activeIndex];
      if (!currentScene) {
        return;
      }

      controller.goToScene(
        currentScene,
        {
          delay,
          duration,
          ease: debouncedEaseValue,
          onComplete: () => {
            // 场景切换完成后调用回调
            store.onComplete(true);
            onComplete && onComplete();
          }
        },
        GLTransitions[transitionIndex]
      );
    },
    [isActive, activeIndex, transitionIndex, delay, duration, debouncedEaseValue, SCENE_LIST, onComplete]
  );

  /**
   * 测试跳转场景
   */
  const goToScene = () => {
    if (!SCENE_LIST || SCENE_LIST.length === 0 || activeIndex >= SCENE_LIST.length) {
      return;
    }

    controller.goToScene(
      SCENE_LIST[activeIndex],
      {
        delay,
        duration,
        ease: debouncedEaseValue,
        onComplete: () => {
          onComplete && onComplete();
        }
      },
      GLTransitions[transitionIndex]
    );
  };

  return (
    <div className={styles.sceneContainer}>
      {isReady &&
        store.width &&
        store.height && (
          <Surface width={store.width} height={customHeight || store.height}>
            <GLTransition {...transitionParams} />
          </Surface>
        )}
    </div>
  );
};
export default wrapStore(Scene, SceneStore);
